import { useEffect, useReducer } from 'react';

import axios from 'axios';

import { SETTINGS_API } from '../config/settingsConfig';
import { type OrgSettings } from '../types/adminConfig';
import { getUrls } from '../utils/url';
import { transformSettingsData } from '../views/AdminSettings/utils/transformSettingsData';

let cache: OrgSettings | null = null;

interface OrgSettingsState {
    loading: boolean;
    error?: Error;
    data?: OrgSettings;
}

const initialState = {
    loading: true,
    error: undefined,
    data: undefined
};

type Action = { type: 'loading' } | { type: 'success'; payload: OrgSettings } | { type: 'error'; payload: Error };

const reducer = (state: OrgSettingsState, action: Action) => {
    switch (action.type) {
        case 'success':
            return { ...state, loading: false, data: action.payload };
        case 'error':
            return { ...state, loading: false, error: action.payload };
        case 'loading':
        default:
            return initialState;
    }
};

export const useOrgSettings = (dumpCache: boolean = false) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        const { api } = getUrls();
        const url = new URL(SETTINGS_API, api);
        if (dumpCache) {
            url.searchParams.append('dumpCache', '1');
        }

        dispatch({ type: 'loading' });

        if (cache && !dumpCache) {
            dispatch({ type: 'success', payload: cache });
            return;
        }

        const getSettings = async () => {
            try {
                const { data: body } = await axios.get(url.toString());
                const { data } = body;

                const settings = transformSettingsData(data);
                cache = settings;

                dispatch({ type: 'success', payload: settings });
            } catch (err) {
                console.error(err);

                dispatch({ type: 'error', payload: err as Error });
            }
        };

        getSettings();
    }, [dumpCache]);

    const updateSettingsState = (updatedSettings: OrgSettings) => {
        cache = updatedSettings;
        dispatch({ type: 'success', payload: updatedSettings });
    };

    return { ...state, updateSettingsState };
};
